import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import axiosInstance from "../../services/axiosInstance";
import { authStorage } from "../../utils/authStorage";
import { loginSuccess, setCustomerData } from "../../store/authSlice";
import { resolveAssetUrl } from "../../utils/assetUrl";
import ProfileSection from "../settings/ProfileSection";
import SecuritySection from "../settings/SecuritySection";
import BrandSection from "../settings/BrandSection";
import AppearanceSection from "../settings/AppearanceSection";
import {
    ChevronRight,
    LockKeyhole,
    Palette,
    Store,
    UserRound,
    X,
} from "lucide-react";


const SECTION_LIST = [
    { key: 'profile', label: 'Profile', description: 'Update your account details and display image.', icon: UserRound },
    { key: 'security', label: 'Account Security', description: 'Manage password and sign-in protection.', icon: LockKeyhole },
    { key: 'brand', label: 'Business/Brand', description: 'Customize business identity and logo.', icon: Store },
    { key: 'appearance', label: 'Theme/Appearance', description: 'Adjust theme colors and interface style.', icon: Palette },
];
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SettingsModal({ isOpen, onClose, onBrandUpdated }) {
    // Section navigation state
    const [section, setSection] = useState(null);
    // ...existing state for each section...
    const [profile, setProfile] = useState({ username: '', email: '', picture: null });
    const [profilePreview, setProfilePreview] = useState(null);
    const [profileError, setProfileError] = useState("");
    const [profileSuccess, setProfileSuccess] = useState("");
    const [profileLoading, setProfileLoading] = useState(false);
    const [removeProfilePicture, setRemoveProfilePicture] = useState(false);
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [passwordSuccess, setPasswordSuccess] = useState("");
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [business, setBusiness] = useState({ name: '', logo: null });
    const [logoPreview, setLogoPreview] = useState(null);
    const [logoError, setLogoError] = useState("");
    const [brandSuccess, setBrandSuccess] = useState("");
    const [brandLoading, setBrandLoading] = useState(false);
    const [removeBrandLogo, setRemoveBrandLogo] = useState(false);

    const dispatch = useDispatch();
    const authToken = useSelector((state) => state.auth.token) || authStorage.getToken();
    const currentUser = useSelector((state) => state.auth.user) || authStorage.getUser();
    const customerId = useSelector((state) => state.auth.customer_id)
        || authStorage.getCustomerId()
        || currentUser?.customer_id
        || null;
    const canManageBrand = currentUser?.user_type === 0 || currentUser?.user_type === 1;
    const canEditUsername = currentUser?.user_type === 0 || currentUser?.user_type === 1;
    const visibleSections = canManageBrand
        ? SECTION_LIST
        : SECTION_LIST.filter((sectionItem) => sectionItem.key !== "brand");

    useEffect(() => {
        if (isOpen) {
            setSection(null);
        }
    }, [isOpen]);

    useEffect(() => {
        if (section === "brand" && !canManageBrand) {
            setSection(null);
        }
    }, [section, canManageBrand]);

    const handleProfileChange = (e) => {
        const { name, value } = e.target;
        setProfile(prev => ({ ...prev, [name]: value }));
    };
    const handleProfilePicChange = (file) => {
        setProfileError("");
        setProfileSuccess("");

        if (!file) {
            setProfile(prev => ({ ...prev, picture: null }));
            setProfilePreview(null);
            setRemoveProfilePicture(true);
            return;
        }

        if (file.size > 2 * 1024 * 1024) {
            setProfileError("Profile picture must be less than 2MB.");
            return;
        }

        setProfile(prev => ({ ...prev, picture: file }));
        setProfilePreview(URL.createObjectURL(file));
        setRemoveProfilePicture(false);
    };
    const handleProfileSave = async (e) => {
        e.preventDefault();
        setProfileError("");
        setProfileSuccess("");

        if (!profile.username.trim() || !profile.email.trim()) {
            setProfileError("Username and email are required.");
            return;
        }

        if (!emailRegex.test(profile.email.trim())) {
            setProfileError("Please enter a valid email address.");
            return;
        }

        setProfileLoading(true);

        try {
            const formData = new FormData();
            formData.append("username", profile.username.trim());
            formData.append("email", profile.email.trim());
            if (profile.picture) {
                formData.append("profile_image", profile.picture);
            }
            if (removeProfilePicture) {
                formData.append("removeProfileImage", "true");
            }

            const response = await axiosInstance.patch("/admin/users/me", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            const updatedUser = {
                ...(currentUser || {}),
                ...(response.data?.user || {}),
            };

            authStorage.setUser(updatedUser);
            if (authToken) {
                dispatch(loginSuccess({ token: authToken, user: updatedUser }));
            }
            window.dispatchEvent(new CustomEvent("user-profile-updated", { detail: updatedUser }));
            setProfile(prev => ({ ...prev, picture: null, username: updatedUser.username || prev.username, email: updatedUser.email || prev.email }));
            setProfilePreview(updatedUser.profile_image || null);
            setRemoveProfilePicture(false);
            setProfileSuccess(response.data?.message || "Profile updated successfully.");
        } catch (error) {
            setProfileError(error.response?.data?.message || "Failed to update profile.");
        } finally {
            setProfileLoading(false);
        }
    };
    const handlePasswordChange = async (e) => {
        e.preventDefault();
        setPasswordError("");
        setPasswordSuccess("");
        if (!currentPassword || !newPassword || !confirmPassword) {
            setPasswordError("All fields are required.");
            return;
        }
        if (newPassword !== confirmPassword) {
            setPasswordError("New passwords do not match.");
            return;
        }

        setPasswordLoading(true);

        try {
            const response = await axiosInstance.patch("/admin/users/change-password", {
                currentPassword,
                newPassword,
                confirmPassword,
            });

            setPasswordSuccess(response.data?.message || "Password changed successfully.");
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
        } catch (error) {
            setPasswordError(error.response?.data?.message || "Failed to change password.");
        } finally {
            setPasswordLoading(false);
        }
    };
    const handleLogoChange = (file) => {
        setLogoError("");
        setBrandSuccess("");

        if (!file) {
            setBusiness(prev => ({ ...prev, logo: null }));
            setLogoPreview(null);
            setRemoveBrandLogo(true);
            return;
        }

        if (file && file.size > 2 * 1024 * 1024) {
            setLogoError("Logo must be less than 2MB.");
            return;
        }
        setBusiness(prev => ({ ...prev, logo: file }));
        setLogoPreview(file ? URL.createObjectURL(file) : null);
        setRemoveBrandLogo(false);
    };
    const handleLogoUpload = async (e) => {
        e.preventDefault();

        setLogoError("");
        setBrandSuccess("");

        if (!business.name.trim() && !business.logo) {
            setLogoError("Business name or logo is required.");
            return;
        }

        setBrandLoading(true);

        try {
            const formData = new FormData();
            formData.append("name", business.name.trim());
            if (business.logo) {
                formData.append("logo", business.logo);
            }
            if (removeBrandLogo) {
                formData.append("removeLogo", "true");
            }

            const response = await axiosInstance.patch("/user/brand", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            const updatedCustomer = response.data?.customer;
            if (updatedCustomer && customerId) {
                const existingCustomer = authStorage.getCustomerData(customerId) || {};
                const mergedCustomer = {
                    ...existingCustomer,
                    ...updatedCustomer,
                };
                authStorage.setCustomerData(customerId, mergedCustomer);
                dispatch(setCustomerData(mergedCustomer));
                onBrandUpdated?.({
                    logo: updatedCustomer.logo,
                    previewUrl: business.logo ? logoPreview : null,
                    updated_at: updatedCustomer.updated_at || new Date().toISOString(),
                });
                window.dispatchEvent(new CustomEvent("customer-brand-updated", {
                    detail: {
                        ...updatedCustomer,
                        previewUrl: business.logo ? logoPreview : null,
                    }
                }));
            } else if (removeBrandLogo) {
                dispatch(setCustomerData({
                    ...(authStorage.getCustomerData(customerId) || {}),
                    logo: null,
                    updated_at: new Date().toISOString(),
                }));
                onBrandUpdated?.({
                    logo: null,
                    previewUrl: null,
                    updated_at: new Date().toISOString(),
                });
            }

            setBusiness(prev => ({ ...prev, logo: null, name: updatedCustomer?.name || prev.name }));
            setLogoPreview(resolveAssetUrl(updatedCustomer?.logo) || null);
            setRemoveBrandLogo(false);
            setBrandSuccess(response.data?.message || "Business/brand updated successfully.");
        } catch (error) {
            setLogoError(error.response?.data?.error || "Failed to update business/brand.");
        } finally {
            setBrandLoading(false);
        }
    };

    useEffect(() => {
        if (!isOpen || section !== "profile") return;

        let isMounted = true;

        const loadProfile = async () => {
            setProfileError("");
            setProfileSuccess("");

            try {
                const response = await axiosInstance.get("/admin/users/me");
                if (!isMounted) return;

                setProfile({
                    username: response.data?.username || "",
                    email: response.data?.email || "",
                    picture: null,
                });
                setProfilePreview(response.data?.profile_image || null);
                setRemoveProfilePicture(false);
            } catch (error) {
                if (isMounted) {
                    setProfileError(error.response?.data?.message || "Failed to load profile.");
                }
            }
        };

        loadProfile();

        return () => {
            isMounted = false;
        };
    }, [isOpen, section]);

    useEffect(() => {
        if (!isOpen || section !== "brand") return;

        let isMounted = true;

        const loadBrand = async () => {
            setLogoError("");
            setBrandSuccess("");

            try {
                const response = await axiosInstance.get("/user/brand");
                if (!isMounted) return;

                const customer = response.data || {};
                setBusiness({ name: customer.name || "", logo: null });
                setLogoPreview(resolveAssetUrl(customer.logo) || null);
                setRemoveBrandLogo(false);

                if (customerId) {
                    const existingCustomer = authStorage.getCustomerData(customerId) || {};
                    const mergedCustomer = { ...existingCustomer, ...customer };
                    authStorage.setCustomerData(customerId, mergedCustomer);
                    dispatch(setCustomerData(mergedCustomer));
                }
            } catch (error) {
                if (isMounted) {
                    setLogoError(error.response?.data?.error || "Failed to load business/brand details.");
                }
            }
        };

        loadBrand();

        return () => {
            isMounted = false;
        };
    }, [isOpen, section, customerId]);

    if (!isOpen || typeof document === "undefined") return null;

    function renderSectionList() {
        return (
            <div className="grid gap-3">
                {visibleSections.map(s => (
                    <button
                        key={s.key}
                        className="group flex w-full items-center justify-between rounded-2xl border border-slate-200 bg-white/80 px-4 py-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300 hover:bg-white hover:shadow-md focus:outline-none"
                        onClick={() => setSection(s.key)}
                    >
                        <div className="flex items-center gap-3">
                            <span
                                className="flex h-11 w-11 items-center justify-center rounded-2xl"
                                style={{
                                    backgroundColor: "color-mix(in srgb, var(--color-primary) 12%, white)",
                                    color: "var(--color-primary)",
                                }}
                            >
                                <s.icon className="h-5 w-5" />
                            </span>
                            <div>
                                <p className="text-sm font-semibold text-slate-900">{s.label}</p>
                                <p className="mt-1 text-xs text-slate-500">{s.description}</p>
                            </div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-slate-600" />
                    </button>
                ))}
            </div>
        );
    }

    function SectionHeader({ title }) {
        return (
            <div className="mb-4 flex items-center gap-3 rounded-2xl border border-slate-200 bg-white/80 px-3 py-3 shadow-sm">
                <button
                    onClick={() => setSection(null)}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-50 hover:text-slate-700 focus:outline-none"
                >
                    <ChevronRight className="h-4 w-4 rotate-180" />
                </button>
                <div>
                    <p className="text-sm font-semibold text-slate-900">{title}</p>
                    <p className="text-xs text-slate-500">Manage and update this settings section.</p>
                </div>
            </div>
        );
    }

    // Section content renderers (minimal, demo only)
    function renderSectionContent() {
        switch (section) {
            case 'profile':
                return <><SectionHeader title="Profile" />
                    <ProfileSection
                        profile={profile}
                        profilePreview={profilePreview}
                        profileError={profileError}
                        profileSuccess={profileSuccess}
                        profileLoading={profileLoading}
                        canEditUsername={canEditUsername}
                        onChange={handleProfileChange}
                        onPicChange={handleProfilePicChange}
                        onSubmit={handleProfileSave}
                    />
                </>;
            case 'security':
                return <><SectionHeader title="Account Security" />
                    <SecuritySection
                        currentPassword={currentPassword}
                        newPassword={newPassword}
                        confirmPassword={confirmPassword}
                        passwordError={passwordError}
                        passwordSuccess={passwordSuccess}
                        passwordLoading={passwordLoading}
                        onCurrentPasswordChange={e => setCurrentPassword(e.target.value)}
                        onNewPasswordChange={e => setNewPassword(e.target.value)}
                        onConfirmPasswordChange={e => setConfirmPassword(e.target.value)}
                        onSubmit={handlePasswordChange}
                    />
                </>;
            case 'brand':
                if (!canManageBrand) return null;
                return <><SectionHeader title="Business/Brand" />
                    <BrandSection
                        business={business}
                        logoPreview={logoPreview}
                        logoError={logoError}
                        brandSuccess={brandSuccess}
                        brandLoading={brandLoading}
                        onNameChange={e => setBusiness(prev => ({ ...prev, name: e.target.value }))}
                        onLogoChange={handleLogoChange}
                        onLogoUpload={handleLogoUpload}
                    />
                </>;
            case 'appearance':
                return <><SectionHeader title="Theme/Appearance" />
                    <AppearanceSection />
                </>;
            default:
                return null;
        }
    }

    return createPortal((
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/45 px-0 py-4 backdrop-blur-sm sm:p-4">
            <div className="relative mx-auto max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-[28px] border border-white/60 bg-gradient-to-br from-white via-slate-50 to-white shadow-[0_24px_80px_rgba(15,23,42,0.22)]">
                <button
                    onClick={onClose}
                    className="absolute right-4 top-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/70 bg-white/95 text-slate-500 shadow-sm transition hover:bg-white hover:text-slate-700 focus:outline-none sm:right-5 sm:top-5"
                    aria-label="Close settings"
                >
                    <X className="h-5 w-5" />
                </button>

                <div className="relative border-b border-slate-200/80 px-5 pb-4 pt-5 sm:px-6 sm:pb-5 sm:pt-6">
                    <div
                        className="pointer-events-none absolute inset-0 rounded-t-[28px] opacity-80"
                        style={{
                            background: "linear-gradient(135deg, color-mix(in srgb, var(--color-primary) 13%, white) 0%, rgba(255,255,255,0) 72%)",
                        }}
                    />
                    <div className="relative flex items-start justify-between gap-4">
                        <div className="min-w-0 pr-14 sm:pr-16">
                            <span className="inline-flex items-center rounded-full border border-white/70 bg-white/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500 shadow-sm">
                                Workspace Controls
                            </span>
                            <h2 className="mt-3 text-[30px] font-semibold tracking-tight leading-none text-slate-900 sm:text-2xl">Settings</h2>
                            <p className="mt-2 max-w-xl text-sm leading-7 text-slate-500 sm:mt-1 sm:leading-6">
                                Refine profile, security, branding, and workspace preferences from one place.
                            </p>
                        </div>
                        <div className="hidden rounded-2xl border border-white/70 bg-white/75 p-3 shadow-sm md:block">
                            <div className="flex h-12 w-12 items-center justify-center rounded-2xl text-white" style={{ background: "var(--color-primary)" }}>
                                <Palette className="h-5 w-5" />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="max-h-[calc(90vh-132px)] overflow-y-auto px-0 py-5 sm:px-6">
                    {section ? (
                        <div className="rounded-[24px] border border-white/70 bg-slate-50/70 px-0 py-4 md:p-5">
                            {renderSectionContent()}
                        </div>
                    ) : (
                        <div className="space-y-4 px-3 sm:px-0">
                            <div className="rounded-2xl border border-white/70 bg-white/75 px-4 py-3 shadow-sm">
                                <p className="text-sm font-medium text-slate-800">Choose a settings area</p>
                                <p className="mt-1 text-xs text-slate-500">
                                    Each section opens in a cleaner workspace so updates feel easier to manage.
                                </p>
                            </div>
                            {renderSectionList()}
                        </div>
                    )}
                </div>
            </div>
        </div>
    ), document.body);
}
